import { supabase } from './supabase';
import { track } from '../utils/analytics';

export interface CheckoutSessionResponse {
  session_id: string;
  url: string;
}

export class StripeService {
  private static instance: StripeService;

  static getInstance(): StripeService {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService();
    }
    return StripeService.instance;
  }

  /**
   * Create a Stripe checkout session for a paid plan (Pro or Prosumer).
   */
  async createCheckoutSession(
    billingCycle: 'monthly' | 'annual',
    plan: 'pro' | 'prosumer' = 'pro',
  ): Promise<CheckoutSessionResponse> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error('Missing auth session');
      }

      track('begin_checkout', { plan, billing_cycle: billingCycle });

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-stripe-checkout`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'apikey': `${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          billingCycle,
          plan,
          mode: 'subscription'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      return {
        session_id: data.session_id,
        url: data.url
      };
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Create customer portal session for managing subscription
   */
  async createPortalSession(): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error('Missing auth session');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-stripe-checkout`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'apikey': `${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'portal'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Get current user's subscription status
   */
  async getSubscriptionStatus(): Promise<{
    tier: string;
    status: string;
    billing_cycle: string | null;
    current_period_end: string | null;
  } | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          status,
          billing_cycle,
          current_period_end,
          subscription_tiers (
            name
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No active subscription
          return null;
        }
        console.error('Error getting subscription status:', error);
        return null;
      }

      return {
        tier: (data.subscription_tiers as any)?.name || 'free',
        status: data.status,
        billing_cycle: data.billing_cycle,
        current_period_end: data.current_period_end
      };
    } catch (error) {
      console.error('Exception getting subscription status:', error);
      return null;
    }
  }
}

export const stripeService = StripeService.getInstance();

